import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Search, Filter } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAudit() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [token]);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.details?.toLowerCase().includes(search.toLowerCase()) ||
      log.action?.toLowerCase().includes(search.toLowerCase()) ||
      log.user_id?.toLowerCase().includes(search.toLowerCase());
    
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    
    return matchesSearch && matchesAction;
  });

  const getActionBadge = (action) => {
    const colors = {
      login: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
      logout: 'bg-gray-500/20 text-gray-500 border-gray-500/30',
      login_failed: 'bg-red-500/20 text-red-500 border-red-500/30',
      password_change: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      pin_change: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      deposit: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30',
      withdrawal: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
      contract_signed: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
      kyc_upload: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30',
      kyc_validated: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30',
      kyc_rejected: 'bg-red-500/20 text-red-500 border-red-500/30',
      settings_changed: 'bg-amber-500/20 text-amber-500 border-amber-500/30',
      mirror_login: 'bg-violet-500/20 text-violet-500 border-violet-500/30'
    };
    
    return (
      <Badge className={`${colors[action] || 'bg-gray-500/20 text-gray-500 border-gray-500/30'} border`}>
        {action?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const uniqueActions = [...new Set(logs.map(l => l.action))].filter(Boolean);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher dans les logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-card"
              data-testid="search-audit-input"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[200px] bg-card" data-testid="action-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les actions</SelectItem>
              {uniqueActions.map(action => (
                <SelectItem key={action} value={action}>
                  {action?.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        <Card className="card-premium">
          <CardHeader>
            <CardTitle className="text-lg">Journal d'audit</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : filteredLogs.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">{t('no_data')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-premium">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>Action</th>
                      <th>Détails</th>
                      <th>Source</th>
                      <th>IP</th>
                      <th>Référence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, index) => (
                      <tr key={log.id || index}>
                        <td className="whitespace-nowrap">
                          <p className="text-sm">{new Date(log.created_at).toLocaleDateString()}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(log.created_at).toLocaleTimeString()}
                          </p>
                        </td>
                        <td>{getActionBadge(log.action)}</td>
                        <td className="max-w-xs">
                          <p className="text-sm truncate">{log.details}</p>
                        </td>
                        <td>
                          <span className="text-xs text-muted-foreground capitalize">
                            {log.source}
                          </span>
                        </td>
                        <td className="font-mono text-xs text-muted-foreground">
                          {log.ip_address || '-'}
                        </td>
                        <td className="font-mono text-xs text-muted-foreground">
                          {log.reference ? log.reference.slice(0, 8) + '...' : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
